import { findConfig } from '@repo/money'
import { formatNumber } from './formatNumber'
import { roundAmount } from './roundAmount'

/** A currency's per-unit display precision, or 2 for unknown currencies. */
const getDisplayPrecision = (currency: string): number => {
  try {
    return findConfig(currency, currency).displayPrecision
  } catch {
    return 2
  }
}

/**
 * Decimal places for displaying a balance/total in a given currency.
 *
 * Fiat (displayPrecision <= 2) keeps a fixed 2 decimals so "$1.00" stays
 * "$1.00". High-precision crypto (displayPrecision > 2) shows up to the
 * currency's precision with trailing zeros trimmed: 1 BTC -> "1",
 * 0.5 -> "0.5", 0.12345678 -> "0.12345678" (no balance hidden).
 *
 * Use this for the balance + stat pills only — chip/bet labels keep their
 * own compact formatter (formatNumberWithSuffix).
 */
export const getCurrencyDecimals = (value: number, currency: string): number => {
  const precision = getDisplayPrecision(currency)
  if (precision <= 2) return 2
  const abs = Math.abs(Number.isFinite(value) ? value : 0)
  const trimmed = abs.toFixed(precision).replace(/0+$/, '').replace(/\.$/, '')
  const dot = trimmed.indexOf('.')
  return dot === -1 ? 0 : trimmed.length - dot - 1
}

/**
 * Compute the minimum decimal places needed to display a non-zero value.
 * Returns at least `min` (default 2), and at most `max` (default 8).
 * For values >= 0.01 this returns 2. For 0.0005 it returns 4, etc.
 */
export const getSmartDecimals = (value: number, min = 2, max = 8): number => {
  const absValue = Math.abs(value)
  if (absValue === 0 || absValue >= Math.pow(10, -min)) return min
  const needed = Math.min(max, -Math.floor(Math.log10(absValue)) + 1)
  return Math.max(min, needed)
}

export const displayAmount = (amount: string | number, format?: boolean, decimals = 2): string => {
  const result = roundAmount(amount, decimals)

  return format ? formatNumber(result, decimals) : result.toFixed(decimals)
}

export const displayMoney = (amount: string | number, format?: boolean): string => {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount
  const decimals = getSmartDecimals(isNaN(numericAmount) ? 0 : numericAmount)
  return displayAmount(amount, format, decimals)
}
